import { useState, type FormEvent } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/i18n/LanguageContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterSignup = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed) || trimmed.length > 255) {
      setError(t("newsletter.invalid"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "subscribe-newsletter",
        {
          body: {
            email: trimmed,
            source: "footer_newsletter",
            language: lang,
          },
        },
      );
      if (invokeError || !data?.ok) {
        setError((data as any)?.error || t("newsletter.error"));
        setSubmitting(false);
        return;
      }
      setSuccess(true);
    } catch {
      setError(t("newsletter.error"));
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-background/50">
          {t("newsletter.title")}
        </p>
        <p className="mt-5 text-sm text-background" aria-live="polite">
          {t("newsletter.success")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-background/50">
        {t("newsletter.title")}
      </p>
      <p className="mt-3 text-sm leading-relaxed text-background/70">
        {t("newsletter.subtext")}
      </p>
      <form onSubmit={handleSubmit} className="mt-3 flex items-stretch gap-2">
        <label htmlFor="newsletter-email" className="sr-only">
          {t("newsletter.placeholder")}
        </label>
        <input
          id="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          placeholder={t("newsletter.placeholder")}
          maxLength={255}
          disabled={submitting}
          autoComplete="email"
          className="h-10 flex-1 min-w-0 rounded-md border border-background/15 bg-background/5 px-3 text-sm text-background placeholder:text-background/40 focus:border-background/40 focus:outline-none focus:ring-1 focus:ring-background/40 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={submitting}
          aria-label={t("newsletter.cta")}
          title={t("newsletter.cta")}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background text-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </form>
      {error ? (
        <p className="mt-2 text-xs text-destructive-foreground/90" role="alert">
          {error}
        </p>
      ) : (
        <p className="mt-2 text-xs text-background/50">{t("newsletter.micro")}</p>
      )}
    </div>
  );
};

export default NewsletterSignup;
